import { logAuditEvent } from '../telemetry/auditTelemetry';
import type { RegistryResolver, ResolvedFieldRecord } from './types';
import { buildAllRegistryResolvers, type SiteResolverContext } from './registryManifest';
import type { ResolvedFieldsStoreAdapter } from './resolvedFieldsStore';
import { valuesEqual } from './resolveCore';

export interface CrossRegistryConflict {
  fieldKey: string;
  registries: Array<{
    registryId: string;
    resolvedValue: unknown;
    resolvedFromTier: number;
  }>;
}

function normalizeFieldKey(fieldKey: string): string {
  return fieldKey.replace(/^[^.]+\./, '').toLowerCase();
}

/** Aggregate resolved records from all registry resolvers. */
export function aggregateResolvedRecords(resolvers: RegistryResolver[]): ResolvedFieldRecord[] {
  return resolvers.flatMap((r) => r.toResolvedRecords());
}

export async function getAllResolvedFields(
  ctx: SiteResolverContext,
  store?: ResolvedFieldsStoreAdapter,
): Promise<ResolvedFieldRecord[]> {
  const resolvers = buildAllRegistryResolvers(ctx);
  const records = aggregateResolvedRecords(resolvers);
  if (store) {
    await store.upsertBatch(records);
  }
  return records;
}

export function findUnresolvedFields(records: ResolvedFieldRecord[]): ResolvedFieldRecord[] {
  return records.filter(
    (r) => r.resolvedValue === null || r.resolvedValue === undefined,
  );
}

export function findTier3DominatedFields(records: ResolvedFieldRecord[]): ResolvedFieldRecord[] {
  return records.filter((r) => r.resolvedFromTier === 3 && r.resolvedValue != null);
}

export function findConflictsAcrossRegistries(
  records: ResolvedFieldRecord[],
): CrossRegistryConflict[] {
  const byNormKey = new Map<string, ResolvedFieldRecord[]>();

  for (const rec of records) {
    if (rec.resolvedValue == null) continue;
    const norm = normalizeFieldKey(rec.fieldKey);
    const bucket = byNormKey.get(norm) ?? [];
    bucket.push(rec);
    byNormKey.set(norm, bucket);
  }

  const conflicts: CrossRegistryConflict[] = [];
  for (const [fieldKey, group] of byNormKey) {
    if (group.length < 2) continue;
    const distinct = group.filter(
      (r, i, arr) => arr.findIndex((x) => valuesEqual(x.resolvedValue, r.resolvedValue)) === i,
    );
    if (distinct.length < 2) continue;
    conflicts.push({
      fieldKey,
      registries: distinct.map((r) => ({
        registryId: r.registryId,
        resolvedValue: r.resolvedValue,
        resolvedFromTier: r.resolvedFromTier,
      })),
    });
  }
  return conflicts;
}

export async function refreshCompositeIndex(
  ctx: SiteResolverContext,
  store: ResolvedFieldsStoreAdapter,
): Promise<{
  records: ResolvedFieldRecord[];
  tier3Dominated: ResolvedFieldRecord[];
  crossRegistryConflicts: CrossRegistryConflict[];
  unresolved: ResolvedFieldRecord[];
}> {
  const records = await getAllResolvedFields(ctx, store);
  const tier3Dominated = findTier3DominatedFields(records);
  const crossRegistryConflicts = findConflictsAcrossRegistries(records);
  const unresolved = findUnresolvedFields(records);

  for (const rec of tier3Dominated) {
    void logAuditEvent({
      type: 'resolver-tier3-dominant-flagged',
      siteId: ctx.site.id,
      payload: {
        fieldKey: rec.fieldKey,
        registryId: rec.registryId,
        resolvedFromTier: rec.resolvedFromTier,
      },
    });
  }

  for (const conflict of crossRegistryConflicts) {
    void logAuditEvent({
      type: 'resolver-cross-registry-conflict',
      siteId: ctx.site.id,
      payload: {
        fieldKey: conflict.fieldKey,
        registries: conflict.registries,
      },
    });
  }

  return { records, tier3Dominated, crossRegistryConflicts, unresolved };
}

/** Load denormalized records from store (no re-resolution). */
export async function loadResolvedFieldsFromStore(
  siteId: string,
  store: ResolvedFieldsStoreAdapter,
): Promise<ResolvedFieldRecord[]> {
  return store.listBySite(siteId);
}
